import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';
import {
  Modal,
  Spinner,
  Button,
  connectModal,
} from '@gsa/afp-component-library';
import { APPROVE_REJECT_MAKE_MODEL } from '../queries.gql';
import { makeModelStatusMessageAtom } from '../atoms/make-model-atoms';
import CommentSection from '../make-model/make-model-modal-comment-section';
import {
  getMakeModelSuccessMessage,
  getMakeModelErrorMessage,
} from '../components/make-model-approval-helper';
import MakeModelSummary from '../components/make-model-summary';

const ApproveRejectConfirmationModal = ({
  data,
  action,
  isOpen,
  onClose,
  refetchData,
}) => {
  const {
    bodyMake,
    bodyModel,
    makeCode,
    modelCode,
    chassisMake,
    chassisModel,
    chassisMakeCode,
    chassisModelCode,
    makeModelApprovalId,
  } = data;
  const [, setToaster] = useRecoilState(makeModelStatusMessageAtom);
  const [rejectCommentBody, setRejectCommentBody] = useState('');

  const isApprove = action === 'Approve';
  const modalTitle = `${isApprove ? 'Approve' : 'Reject'} make and model`;

  const [approveRejectMakeModel, { loading }] = useMutation(
    APPROVE_REJECT_MAKE_MODEL,
    {
      variables: {
        makeModelApprovalInput: {
          makeModelApprovalId: Number.parseInt(makeModelApprovalId, 10),
          makeCode,
          modelCode,
          chassisMakeCode,
          chassisModelCode,
          status: action === 'Approve' ? 'Approved' : 'Rejected',
          comment: rejectCommentBody || '',
        },
      },
      onCompleted: () => {
        onClose();
        setToaster({
          type: 'success',
          message: getMakeModelSuccessMessage(
            action,
            bodyMake.makeName,
            bodyModel.modelName,
            chassisMake?.makeName,
            chassisModel?.modelName,
          ),
        });
        refetchData();
      },
      onError: (err) => {
        onClose();
        setToaster({
          type: 'error',
          message: getMakeModelErrorMessage(
            err.message,
            action,
            bodyMake.makeName,
            bodyModel.modelName,
            chassisMake?.makeName,
            chassisModel?.modelName,
          ),
        });
      },
    },
  );

  return (
    <Modal
      id="remove-standard-item-modal"
      variant="large"
      title={
        <h2 tabIndex="0" aria-label={modalTitle}>
          {modalTitle}
        </h2>
      }
      onClose={onClose}
      isOpen={isOpen}
      actions={
        <div className="flex-align-start flex-justify-start">
          <Button
            data-testid="approve-reject-modal-cancel-btn"
            variant="unstyled"
            onClick={onClose}
            className="padding-right-1"
            label="Cancel"
          />
          <Button
            variant={action === 'Approve' ? 'primary' : 'secondary'}
            onClick={approveRejectMakeModel}
            label={action === 'Approve' ? 'Approve' : 'Reject'}
          />
        </div>
      }
    >
      <>
        {loading && (
          <div
            role="none"
            className="afp-modal-overlay"
            data-testid="approve-reject-modal-loading-spinner"
          >
            <Spinner
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translate(0, -50%)',
              }}
            />
          </div>
        )}
        <div>
          <div className="margin-top-6 margin-bottom-8">
            {!isApprove && (
              <p className="margin-top-0 margin-bottom-4">
                Rejecting this make and model will prevent any bid lines using
                this configuration from being submitted. Vehicle suppliers that
                proposed this make and model in their bid must propose a
                different combination to proceed.
              </p>
            )}
            <span className="margin-bottom-8">
              <strong>
                Are you sure you want to {isApprove ? 'approve' : 'reject'} this
                make and model?
              </strong>
            </span>
            <div className="margin-top-2">
              <MakeModelSummary
                make={{
                  ...bodyMake,
                  makeCode: bodyMake?.makeCode || makeCode,
                }}
                modelName={bodyModel?.modelName}
                modelCode={bodyModel?.modelCode || modelCode}
                nhtsaModelCode={bodyModel?.nhtsaModelCode}
                chassisMake={
                  chassisMake?.makeName
                    ? {
                        ...chassisMake,
                        makeCode: chassisMake?.makeCode || chassisMakeCode,
                      }
                    : null
                }
                chassisModel={
                  chassisModel?.modelName
                    ? {
                        ...chassisModel,
                        modelCode: chassisModel?.modelCode || chassisModelCode,
                      }
                    : null
                }
              />
            </div>
          </div>
          {action === 'Reject' && (
            <CommentSection
              commentBody={rejectCommentBody}
              onCommentChange={setRejectCommentBody}
            />
          )}
        </div>
      </>
    </Modal>
  );
};

ApproveRejectConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.shape(
    PropTypes.objectOf({
      makeCode: PropTypes.number.isRequired,
      makeName: PropTypes.string.isRequired,
      modelCode: PropTypes.string.isRequired,
      modelName: PropTypes.string.isRequired,
      chassisMakeCode: PropTypes.number,
      chassisModelCode: PropTypes.string,
      makeModelApprovalId: PropTypes.number,
    }),
  ).isRequired,
  action: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchData: PropTypes.func.isRequired,
};

const ConnectedMakeModelApprovalModal = connectModal(
  ApproveRejectConfirmationModal,
);

export default ConnectedMakeModelApprovalModal;
